import { useState, useEffect } from 'react';
import Button from '../../components/base/Button';
import SEO from '../../components/SEO';

export default function Partnerships() {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [activePartner, setActivePartner] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    organizationName: '',
    contactPerson: '',
    email: '',
    phone: '',
    organizationType: '',
    partnershipInterest: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActivePartner((prev) => (prev + 1) % partnershipTypes.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formDataToSend = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d5l4nplporqtj2nsciug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          organizationName: '',
          contactPerson: '',
          email: '',
          phone: '',
          organizationType: '',
          partnershipInterest: '',
          description: ''
        });
        setTimeout(() => {
          setShowModal(false);
          setSubmitStatus(null);
        }, 3000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const partnershipTypes = [
    {
      icon: "ri-building-line",
      title: "Corporate Partners",
      description: "Transform industries together through revolutionary technology solutions and strategic collaboration",
      benefits: ["Joint Innovation Projects", "Co-branded Solutions", "Revenue Sharing Models", "Market Expansion"],
      color: "from-purple-500 to-indigo-600"
    },
    {
      icon: "ri-school-line",
      title: "Educational Institutions",
      description: "Revolutionize education with cutting-edge technology infrastructure and training programs",
      benefits: ["DaaS Implementation", "Faculty Training", "Student Programs", "Research Collaboration"],
      color: "from-blue-500 to-cyan-600"
    },
    {
      icon: "ri-government-line",
      title: "Government & NGOs",
      description: "Drive social impact through technology-enabled solutions for communities and public services",
      benefits: ["Public Tech Solutions", "Community Programs", "Policy Innovation", "Social Impact"],
      color: "from-green-500 to-emerald-600"
    },
    {
      icon: "ri-code-box-line",
      title: "Technology Partners",
      description: "Build the future together through integrated solutions and technical collaboration",
      benefits: ["API Integration", "Platform Synergy", "Technical Support", "Joint Development"],
      color: "from-orange-500 to-red-600"
    }
  ];

  const partnershipBenefits = [
    {
      icon: "ri-rocket-line",
      title: "Accelerated Growth",
      description: "Scale faster with combined resources and revolutionary market strategies"
    },
    {
      icon: "ri-lightbulb-line",
      title: "Innovation Access",
      description: "Leverage cutting-edge technology and revolutionary solutions"
    },
    {
      icon: "ri-team-line",
      title: "Expert Network",
      description: "Connect with industry leaders and transformation specialists"
    },
    {
      icon: "ri-trophy-line",
      title: "Market Leadership",
      description: "Dominate markets together through strategic collaboration"
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Revenue Growth",
      description: "Unlock new revenue streams and business opportunities"
    },
    {
      icon: "ri-global-line",
      title: "Global Reach",
      description: "Expand your impact across regions and industries"
    }
  ];

  const organizationTypes = [
    "Educational Institution",
    "Corporate Organization",
    "Government Agency",
    "Non-Profit Organization",
    "Technology Company",
    "Startup",
    "Research Institution",
    "Other"
  ];

  const partnershipInterests = [
    "Device-as-a-Service (DaaS)",
    "Educational Technology Solutions",
    "Software Development Partnership",
    "Training & Capacity Building",
    "Research & Innovation",
    "Market Expansion",
    "Joint Ventures",
    "Technology Integration",
    "Other"
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Partnerships - TheSpire Revolutionary Collaborations",
    "description": "Partner with TheSpire to revolutionize education, technology, and innovation. Join forces with Nigeria's leading tech transformation company.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/partnerships`,
    "mainEntity": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
      "description": "Revolutionary technology solutions provider offering partnerships in education, software development, and innovation"
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <SEO
        title="Partnerships - TheSpire Revolutionary Collaborations"
        description="Partner with TheSpire to revolutionize education, technology, and innovation. Join forces with Nigeria's leading tech transformation company for strategic collaboration."
        keywords="thespire partnerships, tech partnerships nigeria, educational partnerships, corporate collaboration, innovation partnerships, technology alliances"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 25%, transparent 50%)`
        }}
      />
      
      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-[#6D03F4] rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-[#6D03F4]/20 via-transparent to-[#007FFF]/20" />
        
        <div className="relative z-10 max-w-7xl mx-auto text-center">
          <div className="mb-8">
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-full text-sm font-bold mb-6">
              STRATEGIC ALLIANCES
            </span>
          </div>

          <h1 className="text-6xl md:text-8xl lg:text-9xl font-black mb-8 leading-none">
            <span className="block text-white">REVOLUTIONIZE</span>
            <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
              TOGETHER
            </span>
          </h1>
          
          <p className="text-xl md:text-3xl text-gray-300 mb-12 max-w-4xl mx-auto font-light">
            Join forces with <span className="text-[#6D03F4] font-bold">Africa's most revolutionary</span> tech company. 
            Together, we'll <span className="text-[#007FFF] font-bold">transform industries</span> and create unprecedented impact.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <button
              onClick={() => setShowModal(true)}
              className="whitespace-nowrap cursor-pointer font-medium transition-all duration-300 hover:scale-105 bg-[#6D03F4] text-white hover:shadow-lg hover:shadow-[#6D03F4]/30 hover:bg-[#5A02D1] px-8 py-4 text-lg rounded-xl bg-gradient-to-r from-[#6D03F4] to-[#007FFF] hover:from-[#5A02D1] hover:to-[#0066CC] text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              BECOME A PARTNER
            </button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-[#6D03F4] text-[#6D03F4] hover:bg-[#6D03F4] hover:text-white px-12 py-4 text-lg font-bold transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              VIEW OPPORTUNITIES
            </Button>
          </div>
        </div>

        {/* Animated Grid */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(109, 3, 244, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(109, 3, 244, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
            animation: 'grid-move 20s linear infinite'
          }} />
        </div>
      </section>

      {/* Partnership Types */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              PARTNERSHIP
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                OPPORTUNITIES
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Multiple ways to <span className="text-[#6D03F4] font-bold">collaborate and grow</span> together
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {partnershipTypes.map((type, index) => (
              <div 
                key={index} 
                className={`group relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-3xl border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105 ${activePartner === index ? 'ring-2 ring-[#6D03F4]' : ''}`}
              >
                <div className={`w-20 h-20 bg-gradient-to-r ${type.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <i className={`${type.icon} text-3xl text-white`}></i>
                </div>
                
                <h3 className="text-3xl font-bold text-white mb-4">{type.title}</h3>
                <p className="text-gray-300 mb-6 text-lg">{type.description}</p>
                
                <div className="space-y-3 mb-6">
                  {type.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-full"></div>
                      <span className="text-gray-300">{benefit}</span>
                    </div>
                  ))}
                </div>

                <button className="w-full bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white py-3 px-6 rounded-lg hover:from-[#5A02D1] hover:to-[#0066CC] transition-all duration-300 font-bold transform hover:scale-105 whitespace-nowrap">
                  EXPLORE PARTNERSHIP
                </button>
                
                {/* Hover Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4]/20 to-[#007FFF]/20 rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-7xl font-black text-white mb-6">
              WHY PARTNER
              <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
                WITH THESPIRE
              </span>
            </h2>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto">
              Unlock <span className="text-[#6D03F4] font-bold">unprecedented opportunities</span> and accelerate your growth
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {partnershipBenefits.map((benefit, index) => (
              <div key={index} className="group bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm p-8 rounded-2xl border border-white/20 hover:border-[#6D03F4]/50 transition-all duration-500 hover:transform hover:scale-105">
                <div className="w-16 h-16 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{benefit.title}</h3>
                <p className="text-gray-300">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partnership Application Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-[#1A1A1A] to-[#0A0A0A] rounded-3xl border border-white/20 shadow-2xl">
            <div className="sticky top-0 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] p-6 rounded-t-3xl z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-3xl font-black text-white">PARTNERSHIP APPLICATION</h2>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSubmitStatus(null);
                  }}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <i className="ri-close-line text-2xl text-white"></i>
                </button>
              </div>
              <p className="text-white/90 mt-2">Let's revolutionize together - tell us about your organization</p>
            </div>

            <div className="p-8">
              {submitStatus === 'success' && (
                <div className="mb-6 p-4 bg-gradient-to-r from-green-500/20 to-emerald-600/20 border border-green-500/30 text-green-400 rounded-lg text-center">
                  <h3 className="font-bold text-lg mb-2">Partnership Request Received! 🤝</h3>
                  <p>Thank you for your interest! Our partnerships team will review your application and contact you within 48 hours to discuss collaboration opportunities.</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 bg-gradient-to-r from-red-500/20 to-orange-600/20 border border-red-500/30 text-red-400 rounded-lg text-center">
                  <h3 className="font-bold text-lg mb-2">Submission Failed</h3>
                  <p>Something went wrong. Please try again or contact us directly at info@thespire.com.ng</p>
                </div>
              )}

              <form onSubmit={handleSubmit} data-readdy-form id="partnership-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="organizationName" className="block text-sm font-medium text-gray-300 mb-2">
                      Organization Name *
                    </label>
                    <input
                      type="text"
                      id="organizationName"
                      name="organizationName"
                      value={formData.organizationName}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                      placeholder="Your organization name"
                    />
                  </div>

                  <div>
                    <label htmlFor="contactPerson" className="block text-sm font-medium text-gray-300 mb-2">
                      Contact Person *
                    </label>
                    <input
                      type="text"
                      id="contactPerson"
                      name="contactPerson"
                      value={formData.contactPerson}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                      placeholder="Full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                      placeholder="your.email@domain.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400"
                      placeholder="+234 XXX XXX XXXX"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label htmlFor="organizationType" className="block text-sm font-medium text-gray-300 mb-2">
                      Organization Type *
                    </label>
                    <div className="relative">
                      <select
                        id="organizationType"
                        name="organizationType"
                        value={formData.organizationType}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white appearance-none cursor-pointer"
                      >
                        <option value="" className="bg-gray-900 text-white">Select organization type</option>
                        {organizationTypes.map((type, index) => (
                          <option key={index} value={type} className="bg-gray-900 text-white">{type}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="partnershipInterest" className="block text-sm font-medium text-gray-300 mb-2">
                      Partnership Interest *
                    </label>
                    <div className="relative">
                      <select
                        id="partnershipInterest"
                        name="partnershipInterest"
                        value={formData.partnershipInterest}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 pr-8 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white appearance-none cursor-pointer"
                      >
                        <option value="" className="bg-gray-900 text-white">Select partnership area</option>
                        {partnershipInterests.map((interest, index) => (
                          <option key={index} value={interest} className="bg-gray-900 text-white">{interest}</option>
                        ))}
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <label htmlFor="description" className="block text-sm font-medium text-gray-300 mb-2">
                    Partnership Proposal *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    maxLength={500}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6D03F4] text-white placeholder-gray-400 resize-none"
                    placeholder="Tell us about your organization, partnership goals, and how we can create revolutionary impact together..."
                  />
                  <div className="text-xs text-gray-400 mt-1">
                    {formData.description.length}/500 characters
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#6D03F4] to-[#007FFF] text-white py-4 px-8 rounded-lg hover:from-[#5A02D1] hover:to-[#0066CC] transition-all duration-300 font-bold text-lg transform hover:scale-105 disabled:opacity-50 disabled:transform-none whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? 'SUBMITTING APPLICATION...' : 'SUBMIT PARTNERSHIP REQUEST'}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Revolutionary CTA */}
      <section className="py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-[#6D03F4] to-[#007FFF] opacity-20" />
        <div className="relative max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-8">
            READY TO
            <span className="block bg-gradient-to-r from-[#6D03F4] to-[#007FFF] bg-clip-text text-transparent">
              REVOLUTIONIZE TOGETHER?
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12">
            Join <span className="text-[#6D03F4] font-bold">Africa's most innovative</span> tech partnerships network
          </p>
          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <a
              href="https://wa.me/2349167710173"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gradient-to-r from-green-500 to-emerald-600 text-white px-12 py-4 rounded-lg font-bold text-lg hover:from-green-600 hover:to-emerald-700 transition-colors transform hover:scale-105 duration-300 whitespace-nowrap inline-block"
            >
              CHAT WITH US
            </a>
            <a
              href="tel:+2349167710173"
              className="border-2 border-white text-white px-12 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-[#6D03F4] transition-colors transform hover:scale-105 duration-300 whitespace-nowrap inline-block"
            >
              CALL NOW
            </a>
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes grid-move {
          0% { transform: translate(0, 0); }
          100% { transform: translate(50px, 50px); }
        }
      `}</style>
    </div>
  );
}
